/**
 * Global fetch interceptor — dokłada JWT sesji Supabase jako nagłówek
 * `X-Supabase-Auth` do KAŻDEGO zapytania trafiającego w nasze proxy nginx
 * (`/api/*`, `/crawl4ai/*`). nginx `auth_request` czyta ten nagłówek i weryfikuje
 * go przez workera (`/auth-verify`) zanim przepuści request do providera.
 *
 * DLACZEGO własny nagłówek, nie `Authorization`: nginx nadpisuje
 * `Authorization` / `x-api-key` kluczem docelowego API, więc JWT musi jechać
 * nagłówkiem, którego nginx NIE rusza. `X-Supabase-Auth` jest wolny u wszystkich.
 *
 * DLACZEGO owijamy `window.fetch`: 12 miejsc wywołań w 11 plikach uderza w te
 * proxy, a 7 z nich omija wspólny helper `apiBase`. Jeden interceptor pokrywa
 * wszystkie (i przyszłe). Zapytania spoza listy (m.in. supabase-js →
 * `*.supabase.co`) przechodzą bez zmian — brak rekurencji, `X-Workspace` nietknięty.
 */
import { supabase } from "@/integrations/supabase/client";

const PROTECTED_PREFIXES = [
  "/api/openai",
  "/api/anthropic",
  "/api/fal-ai",
  "/api/dataforseo",
  "/api/senuto",
  "/api/geonode",
  "/api/aftermarket",
  "/api/cloudflare",
  "/api/cloudflare-pages",
  "/api/static-build",
  "/crawl4ai",
];

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function isProtected(url: string): boolean {
  return PROTECTED_PREFIXES.some(
    (p) => url.startsWith(p) || url.startsWith(ORIGIN + p),
  );
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

if (typeof window !== "undefined" && typeof window.fetch === "function") {
  const realFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    if (!isProtected(urlOf(input))) return realFetch(input, init);

    // getSession() czyta token z pamięci/localStorage (bez sieci, jeśli ważny).
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return realFetch(input, init); // niezalogowany → nginx zwróci 401

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set("X-Supabase-Auth", `Bearer ${token}`);
    return realFetch(input, { ...init, headers });
  };
}
